import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { api, mutate, uploadFile } from '../shared/api';
import { formatDate, parseDateInputToIso } from '../shared/formatting';
import type { DailyPayload, User } from '../types/biApp';

type Locale = 'hr' | 'en';

const DEFAULT_AVATAR = '/img/default-avatar.jpg';

/** Deterministička boja monograma po imenu. */
function monogramColor(name: string): string {
    let h = 0;
    for (let i = 0; i < name.length; i++) {
        h = (h * 31 + name.charCodeAt(i)) % 360;
    }
    return `hsl(${h}, 45%, 42%)`;
}

function initials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
}

/** Avatar — foto → default-avatar.jpg → monogram fallback. */
export function Avatar({ user, size = 40 }: { user: Pick<User, 'name'> & { profile?: { photo_url: string | null } }; size?: number }) {
    const [stage, setStage] = useState<'photo' | 'default' | 'monogram'>(
        user.profile?.photo_url ? 'photo' : 'default',
    );
    const src = stage === 'photo' ? user.profile?.photo_url ?? '' : DEFAULT_AVATAR;

    if (stage === 'monogram') {
        return (
            <div
                className="bi-avatar bi-avatar-monogram"
                style={{ width: size, height: size, background: monogramColor(user.name), fontSize: size * 0.4 }}
            >
                {initials(user.name)}
            </div>
        );
    }

    return (
        <img
            className="bi-avatar"
            src={src}
            alt={user.name}
            width={size}
            height={size}
            style={{ width: size, height: size }}
            onError={() => setStage((s) => (s === 'photo' ? 'default' : 'monogram'))}
        />
    );
}

/** Topbar kartica korisnika — foto, ime, jezik dropdown, uredi profil, logout. */
export function ProfileTopCard({
    user,
    locale,
    onLocaleChange,
    onOpenProfile,
    onLogout,
    nowLabel,
    dechaosInboundEmail,
    hedgeAlert,
    onOpenHedge,
    vpnBadge,
}: {
    user: User;
    locale: Locale;
    onLocaleChange: (l: Locale) => void;
    onOpenProfile: () => void;
    onLogout: () => void;
    nowLabel: string;
    dechaosInboundEmail?: string | null;
    hedgeAlert?: { unhedged_count?: number | null; oldest_min?: number | null; fixed_needs_release?: number | null } | null;
    onOpenHedge?: () => void;
    vpnBadge?: boolean;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const incomplete = Boolean(user.profile_incomplete);

    return (
        <div className="user-card card bi-usercard">
            <div className="bi-usercard-id">
                <button
                    type="button"
                    className="bi-avatar-btn"
                    onClick={onOpenProfile}
                    title={t('Uredi profil', 'Edit profile')}
                >
                    <Avatar user={user} size={52} />
                    {incomplete ? <span className="bi-avatar-warn" title={t('Profil je nepotpun', 'Profile incomplete')}>!</span> : null}
                </button>
                <div className="bi-usercard-meta">
                    <strong>{user.name}</strong>
                    <span className="muted">{user.role}</span>
                    {hedgeAlert ? (
                        <button type="button" onClick={onOpenHedge}
                            title={locale === 'en' ? 'Unhedged orders >30 min — go hedge' : 'Nehedgirane narudžbe >30 min — odhedgaj ručno'}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#b14040', color: '#fff', border: 0, borderRadius: 6, padding: '3px 9px', fontWeight: 700, cursor: 'pointer', fontSize: '0.74rem', alignSelf: 'flex-start', margin: '2px 0' }}>
                            ⚠ {[
                                (hedgeAlert.unhedged_count ?? 0) > 0 ? `${hedgeAlert.unhedged_count} ${locale === 'en' ? 'unhedged' : 'nehedg.'} ${hedgeAlert.oldest_min}′` : null,
                                (hedgeAlert.fixed_needs_release ?? 0) > 0 ? `🔓 ${hedgeAlert.fixed_needs_release} ${locale === 'en' ? 'to release' : 'za otpustiti'}` : null,
                            ].filter(Boolean).join(' · ')}
                        </button>
                    ) : null}
                    {dechaosInboundEmail ? (
                        <span title={t('DeChaos inbound email — pošlji mail ovdje da kreira intake', 'DeChaos inbound email — send mail here to create intake')}
                            style={{ fontSize: '0.7rem', color: '#b14040', fontFamily: 'ui-monospace, monospace', cursor: 'pointer' }}
                            onClick={() => navigator.clipboard?.writeText(dechaosInboundEmail)}>
                            🌪 {dechaosInboundEmail} <span style={{ opacity: 0.6 }}>(klik = kopiraj)</span>
                        </span>
                    ) : null}
                    <span className="muted bi-usercard-time">
                        {nowLabel}
                        {vpnBadge ? (
                            <span style={{ color: '#1f7a36', fontWeight: 700, marginLeft: 6 }}
                                title={t('Pristup preko Aurodomus VPN-a', 'Access via Aurodomus VPN')}>
                                · vpn ✓
                            </span>
                        ) : null}
                    </span>
                </div>
            </div>
            <div className="bi-usercard-actions">
                <button type="button" className="bi-editprofile-btn" onClick={onOpenProfile}>
                    {incomplete ? '⚠ ' : '👤 '}{t('Uredi profil', 'Edit profile')}
                </button>
                <select
                    className="bi-lang-select"
                    value={locale}
                    onChange={(e) => onLocaleChange(e.target.value === 'en' ? 'en' : 'hr')}
                    aria-label={t('Jezik', 'Language')}
                >
                    <option value="hr">🇭🇷 HR</option>
                    <option value="en">🇬🇧 EN</option>
                </select>
                <button type="button" className="secondary compact" onClick={onLogout}>
                    {t('Odjava', 'Logout')}
                </button>
                {vpnBadge ? (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'linear-gradient(135deg, #2ecc71 0%, #1f7a36 100%)',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: '0.7rem',
                        padding: '5px 12px',
                        borderRadius: 14,
                        letterSpacing: '0.06em',
                        boxShadow: '0 2px 8px rgba(31,122,54,0.35)',
                        textTransform: 'uppercase',
                    }} title={t('VPN woohoo — siguran tunel', 'VPN woohoo — secure tunnel')}>
                        🛡 VPN woohoo
                    </span>
                ) : null}
            </div>
        </div>
    );
}

type AccordionProps = {
    title: string;
    subtitle?: string;
    defaultOpen?: boolean;
    badge?: React.ReactNode;
    children: React.ReactNode;
};

function Accordion({ title, subtitle, defaultOpen = false, badge, children }: AccordionProps) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={`bi-accordion ${open ? 'is-open' : ''}`}>
            <button type="button" className="bi-accordion-head" onClick={() => setOpen((o) => !o)}>
                <span className="bi-accordion-title">
                    {title}
                    {badge}
                </span>
                {subtitle ? <span className="bi-accordion-sub muted">{subtitle}</span> : null}
                <span className="bi-accordion-chevron">{open ? '▾' : '▸'}</span>
            </button>
            {open ? <div className="bi-accordion-body">{children}</div> : null}
        </div>
    );
}

function Field({
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    readOnly,
    hint,
}: {
    label: string;
    value: string;
    onChange?: (v: string) => void;
    type?: string;
    placeholder?: string;
    readOnly?: boolean;
    hint?: string;
}) {
    return (
        <label className="bi-field">
            <span className="bi-field-label">{label}</span>
            <input
                type={type}
                value={value}
                placeholder={placeholder}
                readOnly={readOnly}
                onChange={(e) => onChange?.(e.target.value)}
                className={readOnly ? 'bi-input-ro' : ''}
            />
            {hint ? <span className="bi-field-hint muted">{hint}</span> : null}
        </label>
    );
}

/**
 * Datumsko polje — prikaz/unos UVIJEK dd.mm.yyyy (BI foundation pravilo).
 * Parent state ostaje ISO yyyy-mm-dd; konverzija samo na UI granici.
 */
function DateField({
    label,
    isoValue,
    onChange,
    hint,
}: {
    label: string;
    isoValue: string;
    onChange: (iso: string) => void;
    hint?: string;
}) {
    const [text, setText] = useState(() => (isoValue ? formatDate(isoValue, 'hr') : ''));
    const [parseError, setParseError] = useState(false);

    useEffect(() => {
        setText(isoValue ? formatDate(isoValue, 'hr') : '');
        setParseError(false);
    }, [isoValue]);

    function commit(raw: string) {
        const trimmed = raw.trim();
        if (trimmed === '') {
            setParseError(false);
            onChange('');
            return;
        }
        const iso = parseDateInputToIso(trimmed);
        if (iso) {
            setParseError(false);
            // Reformatiraj input u kanonski dd.mm.yyyy oblik (npr. "02081979" → "02.08.1979")
            setText(formatDate(iso, 'hr'));
            onChange(iso);
        } else {
            setParseError(true);
        }
    }

    return (
        <label className="bi-field">
            <span className="bi-field-label">{label}</span>
            <input
                type="text"
                inputMode="numeric"
                value={text}
                placeholder="dd.mm.yyyy"
                onChange={(e) => { setText(e.target.value); if (parseError) setParseError(false); }}
                onBlur={(e) => commit(e.target.value)}
                style={parseError ? { borderColor: '#b14040', background: 'rgba(177,64,64,0.05)' } : undefined}
            />
            {parseError ? (
                <span className="bi-field-hint" style={{ color: '#b14040', fontWeight: 600 }}>
                    ⚠ Neispravan format. Probaj: 02.08.1979 ili 02081979
                </span>
            ) : hint ? <span className="bi-field-hint muted">{hint}</span> : null}
        </label>
    );
}

type ProfileForm = {
    job_title: string;
    show_in_directory: boolean;
    share_birthday_in_directory: boolean;
    share_photo_in_directory: boolean;
    share_phone_in_directory: boolean;
    share_email_in_directory: boolean;
    share_job_title_in_directory: boolean;
    dechaos_enabled: boolean;
    name: string;
    phone_business: string;
    phone_private: string;
    address: string;
    address_home: string;
    address_work: string;
    email_private: string;
    date_of_birth: string;
};

const EMPTY_FORM: ProfileForm = {
    name: '', phone_business: '', phone_private: '', address: '',
    address_home: '', address_work: '', email_private: '', date_of_birth: '',
    job_title: '', show_in_directory: false, share_birthday_in_directory: false,
    share_photo_in_directory: true, share_phone_in_directory: true,
    share_email_in_directory: true, share_job_title_in_directory: true,
    dechaos_enabled: false,
};

/** Glavna stranica profila — accordion sekcije + dosjetka + horoskop. */
export function ProfileModule({
    user,
    token,
    locale,
    onUserUpdated,
    onClose,
}: {
    user: User;
    token: string;
    locale: Locale;
    onUserUpdated: (u: User) => void;
    onClose: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isSuperVjakomin = user.role === 'superadministrator' && user.email === 'vjakomin@gmail.com';
    const isSuperadmin = user.role === 'superadministrator';

    const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
    const [pwd, setPwd] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [daily, setDaily] = useState<DailyPayload | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [photoBusy, setPhotoBusy] = useState(false);
    const [lightbox, setLightbox] = useState(false);

    useEffect(() => {
        const p = user.profile;
        setForm({
            name: user.name ?? '',
            phone_business: p?.phone_business ?? '',
            phone_private: p?.phone_private ?? '',
            address: p?.address ?? '',
            address_home: p?.address_home ?? '',
            address_work: p?.address_work ?? '',
            email_private: p?.email_private ?? '',
            date_of_birth: p?.date_of_birth ?? '',
            job_title: (p as { job_title?: string | null } | undefined)?.job_title ?? '',
            show_in_directory: Boolean((p as { show_in_directory?: boolean } | undefined)?.show_in_directory),
            share_birthday_in_directory: Boolean((p as { share_birthday_in_directory?: boolean } | undefined)?.share_birthday_in_directory),
            share_photo_in_directory: (p as { share_photo_in_directory?: boolean } | undefined)?.share_photo_in_directory ?? true,
            share_phone_in_directory: (p as { share_phone_in_directory?: boolean } | undefined)?.share_phone_in_directory ?? true,
            share_email_in_directory: (p as { share_email_in_directory?: boolean } | undefined)?.share_email_in_directory ?? true,
            share_job_title_in_directory: (p as { share_job_title_in_directory?: boolean } | undefined)?.share_job_title_in_directory ?? true,
            dechaos_enabled: Boolean((p as { dechaos_enabled?: boolean } | undefined)?.dechaos_enabled),
        });
    }, [user]);

    useEffect(() => {
        api<DailyPayload>('/me/daily', token).then(setDaily).catch(() => undefined);
    }, [token]);

    const set = (k: keyof ProfileForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        setErr('');
        setMsg('');
        if (pwd && pwd !== pwd2) {
            setErr(t('Lozinke se ne podudaraju.', 'Passwords do not match.'));
            return;
        }
        if (pwd && pwd.length < 10) {
            setErr(t('Lozinka mora imati barem 10 znakova.', 'Password must be at least 10 characters.'));
            return;
        }
        setSaving(true);
        try {
            const body: Record<string, unknown> = { ...form };
            if (pwd) body.password = pwd;
            const res = await mutate<{ user: User }>('/me/profile', token, 'PATCH', body);
            onUserUpdated(res.user);
            setPwd('');
            setPwd2('');
            setMsg(t('Profil spremljen.', 'Profile saved.'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Error');
        } finally {
            setSaving(false);
        }
    }

    async function uploadPhoto(file: File) {
        setErr('');
        setMsg('');
        setPhotoBusy(true);
        try {
            const fd = new FormData();
            fd.append('photo', file);
            const res = await uploadFile<{ photo_url: string; size_kb: number }>('/me/photo', token, fd);
            onUserUpdated({ ...user, profile: { ...(user.profile as User['profile'])!, photo_url: res.photo_url } });
            setMsg(t(`Fotografija spremljena (${res.size_kb} KB).`, `Photo saved (${res.size_kb} KB).`));
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Error');
        } finally {
            setPhotoBusy(false);
        }
    }

    async function removePhoto() {
        setPhotoBusy(true);
        try {
            await mutate('/me/photo', token, 'DELETE');
            onUserUpdated({ ...user, profile: { ...(user.profile as User['profile'])!, photo_url: null } });
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Error');
        } finally {
            setPhotoBusy(false);
        }
    }

    const incomplete = Boolean(user.profile_incomplete);

    return (
        <div className="bi-profile">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">👤</div>
                <div className="bi-module-hero-text">
                    <h1>{t('Moj profil', 'My profile')}</h1>
                    <p className="muted">
                        {t(
                            'Osobni podaci, fotografija i postavke. Osoba zaduženja u sustavu = tvoj korisnički profil.',
                            'Personal data, photo and settings. Your responsible-person record = this profile.',
                        )}
                    </p>
                </div>
                <button type="button" className="bi-profile-close" onClick={onClose}>
                    ✕ {t('Zatvori profil', 'Close profile')}
                </button>
            </div>

            {incomplete ? (
                <div className="bi-profile-alert">
                    ⚠ {t(
                        'Profil je nepotpun — molimo unesi barem poslovni telefon i datum rođenja.',
                        'Profile incomplete — please enter at least your business phone and date of birth.',
                    )}
                </div>
            ) : null}

            <div className="bi-profile-photo card">
                <button
                    type="button"
                    className="bi-avatar-zoom"
                    onClick={() => setLightbox(true)}
                    title={t('Povećaj fotografiju', 'Enlarge photo')}
                >
                    <Avatar user={user} size={104} />
                    <span className="bi-avatar-zoom-icon">🔍</span>
                </button>
                <div className="bi-profile-photo-actions">
                    <strong>{user.name}</strong>
                    <span className="muted">{user.email}</span>
                    <div className="bi-profile-photo-btns">
                        <button
                            type="button"
                            className="compact"
                            disabled={photoBusy}
                            onClick={() => fileRef.current?.click()}
                        >
                            {photoBusy ? '⏳' : '📷'} {t('Promijeni fotografiju', 'Change photo')}
                        </button>
                        {user.profile?.photo_url ? (
                            <button type="button" className="secondary compact" disabled={photoBusy} onClick={() => void removePhoto()}>
                                {t('Ukloni', 'Remove')}
                            </button>
                        ) : null}
                    </div>
                    <span className="muted bi-field-hint">
                        {t('Slika se automatski smanjuje na ≤100 KB.', 'Image auto-resized to ≤100 KB.')}
                    </span>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void uploadPhoto(f);
                            e.target.value = '';
                        }}
                    />
                </div>
            </div>

            {err ? <p className="error">{err}</p> : null}
            {msg ? <p className="bi-profile-ok">{msg}</p> : null}

            <Accordion
                title={t('Osnovni podaci', 'Basic data')}
                defaultOpen
                subtitle={t('Ime, email, datum rođenja', 'Name, email, date of birth')}
            >
                <div className="bi-field-grid">
                    <Field label={t('Ime i prezime', 'Full name')} value={form.name} onChange={set('name')} />
                    <Field label={t('Radno mjesto (naziv)', 'Job title')} value={form.job_title} onChange={set('job_title')} hint={t('Npr. Voditelj poslovnice, Računovođa, Direktor', 'e.g. Branch Manager, Accountant, CEO')} />
                    <Field
                        label={t('Poslovni email (korisničko ime)', 'Business email (username)')}
                        value={user.email}
                        readOnly
                        hint={t('Mijenja administrator', 'Changed by administrator')}
                    />
                    <Field label={t('Privatni email', 'Private email')} type="email" value={form.email_private} onChange={set('email_private')} />
                    <DateField
                        label={t('Datum rođenja', 'Date of birth')}
                        isoValue={form.date_of_birth}
                        onChange={set('date_of_birth')}
                        hint={!form.date_of_birth ? t('Potrebno za horoskop i čestitku · dd.mm.yyyy', 'Needed for horoscope & birthday · dd.mm.yyyy') : undefined}
                    />
                </div>
            </Accordion>

            <Accordion
                title={t('Imenik', 'Directory')}
                subtitle={form.show_in_directory ? t('U imeniku si — možeš urediti što dijeliš', 'You are in the directory — edit what you share') : t('Pridruži se internom imeniku', 'Join the internal directory')}
                defaultOpen
                badge={form.show_in_directory ? <span style={{ marginLeft: 8, fontSize: '0.72rem', background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>✓ {t('uključen', 'on')}</span> : <span style={{ marginLeft: 8, fontSize: '0.72rem', background: 'rgba(var(--ad-acc-rgb),0.18)', color: 'var(--ad-acc-3)', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>📖 {t('još nisi', 'not yet')}</span>}
            >
                <DirectorySection form={form} setForm={setForm} locale={locale} token={token} onSaved={onUserUpdated} />
            </Accordion>

            <Accordion title={t('Kontakt', 'Contact')} subtitle={t('Telefonski brojevi', 'Phone numbers')}>
                <div className="bi-field-grid">
                    <Field
                        label={t('Telefon poslovni', 'Business phone')}
                        value={form.phone_business}
                        onChange={set('phone_business')}
                        hint={!form.phone_business ? t('Obavezno polje', 'Required field') : undefined}
                    />
                    <Field label={t('Telefon privatni', 'Private phone')} value={form.phone_private} onChange={set('phone_private')} />
                </div>
            </Accordion>

            <Accordion title={t('Adrese', 'Addresses')} subtitle={t('Adresa, kućna i radna adresa', 'Address, home & work')}>
                <div className="bi-field-grid">
                    <Field label={t('Adresa', 'Address')} value={form.address} onChange={set('address')} />
                    <Field label={t('Kućna adresa', 'Home address')} value={form.address_home} onChange={set('address_home')} />
                    <Field label={t('Adresa radnog mjesta', 'Work address')} value={form.address_work} onChange={set('address_work')} />
                </div>
            </Accordion>

            <Accordion title={t('Lozinka', 'Password')} subtitle={t('Promjena lozinke', 'Change password')}>
                <div className="bi-field-grid">
                    <Field label={t('Nova lozinka', 'New password')} type="password" value={pwd} onChange={setPwd} hint={t('Min. 10 znakova', 'Min. 10 characters')} />
                    <Field label={t('Ponovi lozinku', 'Repeat password')} type="password" value={pwd2} onChange={setPwd2} />
                </div>
            </Accordion>

            <TwoFactorAccordion token={token} locale={locale} />

            <Accordion title={t('🌪 DeChaos integracija', '🌪 DeChaos integration')} subtitle={t('Triaža, intake email, AI pipeline iz m.11 Zadaci', 'Triage, intake email, AI pipeline from m.11 Tasks')} defaultOpen={form.dechaos_enabled}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: '0.92rem' }}>
                    <input type="checkbox" checked={!!form.dechaos_enabled} onChange={(e) => setForm((f) => ({ ...f, dechaos_enabled: e.target.checked }))} />
                    <span><strong>{t('Uključi DeChaos integraciju', 'Enable DeChaos integration')}</strong></span>
                </label>
                <p className="muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
                    {t('Kad je uključeno: m.11 Zadaci dobiva DeChaos tab s pravim taskovima/triažom, dobiješ vlastiti intake email (ime.prezime@…) i možeš direktno slati mail-ove + AI pretvori u taskove. Spremi profil za primjenu.', 'When enabled: m.11 Tasks gets a DeChaos tab with real tasks/triage, you receive your own intake email (firstname.lastname@…), and you can send mails directly + AI converts to tasks. Save profile to apply.')}
                </p>
            </Accordion>

            <AsanaConnectionAccordion token={token} locale={locale} />

            {isSuperVjakomin ? <GlobalSettingsAccordion token={token} locale={locale} /> : null}
            {isSuperadmin ? <AdoSalesSettingsAccordion token={token} locale={locale} /> : null}
            {isSuperadmin ? <HedgeToggleAccessAccordion token={token} locale={locale} /> : null}
            {isSuperVjakomin ? <OwnerNotificationsAccordion token={token} locale={locale} /> : null}
            {isSuperVjakomin ? <ErrorPagesAccordion token={token} locale={locale} /> : null}

            <div className="bi-profile-savebar">
                <button type="button" disabled={saving} onClick={() => void save()}>
                    {saving ? t('Spremam…', 'Saving…') : t('Spremi profil', 'Save profile')}
                </button>
            </div>

            <DailyCard daily={daily} locale={locale} hasDob={Boolean(form.date_of_birth || user.profile?.date_of_birth)} />

            {lightbox ? (
                <div className="bi-lightbox" onClick={() => setLightbox(false)} role="dialog" aria-modal="true">
                    <button type="button" className="bi-lightbox-close" onClick={() => setLightbox(false)} aria-label={t('Zatvori', 'Close')}>✕</button>
                    <img
                        className="bi-lightbox-img"
                        src={user.profile?.photo_url ?? DEFAULT_AVATAR}
                        alt={user.name}
                        onClick={(e) => e.stopPropagation()}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }}
                    />
                </div>
            ) : null}
        </div>
    );
}

/**
 * Imenik sekcija profila — korisnik se sam može dodati u imenik (m.170),
 * uz per-field kvačice koje informacije želi podijeliti s ostalim suradnicima.
 * Ime + tvrtka/poslovnica se uvijek prikazuju — to je svrha imenika.
 */
function DirectorySection({
    form, setForm, locale, token, onSaved,
}: {
    form: ProfileForm;
    setForm: React.Dispatch<React.SetStateAction<ProfileForm>>;
    locale: Locale;
    token: string;
    onSaved: (u: User) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [busy, setBusy] = useState(false);

    async function patchDirectoryFields(updates: Partial<ProfileForm>) {
        setBusy(true);
        try {
            const res = await mutate<{ user: User }>('/me/profile', token, 'PATCH', updates);
            onSaved(res.user);
            setForm((f) => ({ ...f, ...updates }));
        } catch (e) {
            // Greška se prikazuje u glavnom save flow-u; tihi fallback.
            console.error('directory patch failed', e);
        } finally {
            setBusy(false);
        }
    }

    async function joinDirectory(opts: {
        share_photo_in_directory: boolean;
        share_phone_in_directory: boolean;
        share_email_in_directory: boolean;
        share_job_title_in_directory: boolean;
        share_birthday_in_directory: boolean;
    }) {
        await patchDirectoryFields({ show_in_directory: true, ...opts });
        setShowJoinModal(false);
    }

    async function leaveDirectory() {
        if (!confirm(t('Sigurno se želiš ukloniti iz imenika?', 'Are you sure you want to leave the directory?'))) return;
        await patchDirectoryFields({ show_in_directory: false });
    }

    if (!form.show_in_directory) {
        return (
            <>
                <p className="muted" style={{ fontSize: '0.86rem', margin: '0 0 16px' }}>
                    {t(
                        'Imenik je interna lista suradnika — pomaže kolegama da brzo nađu tvoj kontakt. Pridruživanje je dobrovoljno i kontroliraš koje informacije dijeliš.',
                        'The directory is an internal staff list — it helps colleagues quickly find your contact. Joining is voluntary and you control what you share.',
                    )}
                </p>
                <button
                    type="button"
                    onClick={() => setShowJoinModal(true)}
                    disabled={busy}
                    style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 50%, var(--ad-acc) 100%)',
                        color: '#fffbeb',
                        border: 0,
                        borderRadius: 14,
                        padding: '18px 24px',
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        cursor: busy ? 'wait' : 'pointer',
                        boxShadow: '0 10px 30px rgba(107,79,28,0.25)',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
                >
                    <span style={{ fontSize: '1.3rem' }}>📖</span>
                    {t('Pridruži se imeniku', 'Join the directory')}
                </button>
                {showJoinModal ? (
                    <DirectoryJoinModal
                        form={form}
                        locale={locale}
                        onConfirm={joinDirectory}
                        onCancel={() => setShowJoinModal(false)}
                    />
                ) : null}
            </>
        );
    }

    // Već u imeniku — pokaži per-field toggleove + leave link
    const togglesConfig: Array<{ key: keyof ProfileForm; icon: string; label: string }> = [
        { key: 'share_photo_in_directory', icon: '📷', label: t('Pokaži moju fotografiju', 'Show my photo') },
        { key: 'share_job_title_in_directory', icon: '💼', label: t('Pokaži radno mjesto', 'Show job title') },
        { key: 'share_email_in_directory', icon: '📧', label: t('Pokaži službeni email', 'Show business email') },
        { key: 'share_phone_in_directory', icon: '📞', label: t('Pokaži poslovni telefon', 'Show business phone') },
        { key: 'share_birthday_in_directory', icon: '🎂', label: t('Pokaži datum rođenja', 'Show date of birth') },
    ];

    return (
        <>
            <div style={{
                background: 'rgba(31,122,54,0.08)',
                border: '1px solid rgba(31,122,54,0.25)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: '0.88rem',
                color: '#1f7a36',
            }}>
                <span style={{ fontSize: '1.1rem' }}>✓</span>
                <strong>{t('U imeniku si.', 'You are in the directory.')}</strong>
                <span style={{ marginLeft: 'auto' }}>
                    <button
                        type="button"
                        onClick={() => void leaveDirectory()}
                        disabled={busy}
                        style={{
                            background: 'transparent',
                            border: 0,
                            color: '#b14040',
                            cursor: 'pointer',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            padding: 0,
                        }}
                    >
                        {t('Ukloni se iz imenika', 'Leave the directory')}
                    </button>
                </span>
            </div>
            <p className="muted" style={{ fontSize: '0.82rem', margin: '0 0 12px' }}>
                {t(
                    'Tvoje ime i matična tvrtka/poslovnica se uvijek prikazuju. Ostale informacije možeš sakriti pojedinačno.',
                    'Your name and home company/branch are always shown. Other info you can hide per item.',
                )}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {togglesConfig.map((cfg) => {
                    const value = Boolean(form[cfg.key]);
                    return (
                        <button
                            key={cfg.key}
                            type="button"
                            className={`bi-toggle-row ${value ? 'is-on' : ''}`}
                            onClick={() => void patchDirectoryFields({ [cfg.key]: !value } as Partial<ProfileForm>)}
                            disabled={busy}
                        >
                            <span>{cfg.icon} {cfg.label}</span>
                            <span className={`bi-toggle-switch ${value ? 'is-on' : ''}`}>
                                <span className="bi-toggle-knob" />
                            </span>
                        </button>
                    );
                })}
            </div>
        </>
    );
}

/**
 * Modal koji se otvara kad korisnik klikne "Pridruži se imeniku".
 * Inicijalno sve kvačice ON — najtipičniji case. Korisnik može isključiti pojedine.
 */
function DirectoryJoinModal({
    form, locale, onConfirm, onCancel,
}: {
    form: ProfileForm;
    locale: Locale;
    onConfirm: (opts: {
        share_photo_in_directory: boolean;
        share_phone_in_directory: boolean;
        share_email_in_directory: boolean;
        share_job_title_in_directory: boolean;
        share_birthday_in_directory: boolean;
    }) => Promise<void>;
    onCancel: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [sharePhoto, setSharePhoto] = useState(form.share_photo_in_directory);
    const [sharePhone, setSharePhone] = useState(form.share_phone_in_directory);
    const [shareEmail, setShareEmail] = useState(form.share_email_in_directory);
    const [shareJob, setShareJob] = useState(form.share_job_title_in_directory);
    const [shareBirthday, setShareBirthday] = useState(form.share_birthday_in_directory);
    const [submitting, setSubmitting] = useState(false);

    const opts = [
        { value: sharePhoto, setter: setSharePhoto, icon: '📷', label: t('Fotografija', 'Photo'), hint: t('Tvoja profilna slika. Ako isključiš, prikaže se default avatar.', 'Your profile photo. If off, default avatar shows.') },
        { value: shareJob, setter: setShareJob, icon: '💼', label: t('Radno mjesto', 'Job title'), hint: t('Npr. „Voditelj poslovnice"', 'e.g. "Branch Manager"') },
        { value: shareEmail, setter: setShareEmail, icon: '📧', label: t('Službeni email', 'Business email'), hint: t('Klikom na njega kolega ti pošalje mail.', 'Colleagues can email you with one click.') },
        { value: sharePhone, setter: setSharePhone, icon: '📞', label: t('Poslovni telefon', 'Business phone'), hint: t('Klikom na njega kolega zove tvoj broj.', 'Colleagues can call you with one click.') },
        { value: shareBirthday, setter: setShareBirthday, icon: '🎂', label: t('Datum rođenja', 'Date of birth'), hint: t('Kolege mogu vidjeti tvoj rođendan kad se približi.', 'Colleagues can see your birthday as it approaches.') },
    ];

    async function submit() {
        setSubmitting(true);
        try {
            await onConfirm({
                share_photo_in_directory: sharePhoto,
                share_phone_in_directory: sharePhone,
                share_email_in_directory: shareEmail,
                share_job_title_in_directory: shareJob,
                share_birthday_in_directory: shareBirthday,
            });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(560px, 96vw)',
                maxHeight: '92vh',
                overflowY: 'auto',
                background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                borderRadius: 16,
                boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    padding: '1.1rem 1.5rem',
                    color: '#fffbeb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                            {t('Pridruživanje imeniku', 'Joining the directory')}
                        </p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.3rem', fontWeight: 700 }}>
                            {t('Što želiš podijeliti?', 'What do you want to share?')}
                        </h2>
                    </div>
                    <button type="button" onClick={onCancel} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}>×</button>
                </div>
                <div style={{ padding: '1.2rem 1.5rem' }}>
                    <p className="muted" style={{ fontSize: '0.86rem', margin: '0 0 16px' }}>
                        {t(
                            'Tvoje ime i tvrtka/poslovnica uvijek se prikazuju. Sve ostalo je tvoja odluka i možeš ju kasnije promijeniti.',
                            'Your name and company/branch are always shown. Everything else is your choice and you can change it later.',
                        )}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {opts.map((o, idx) => (
                            <label key={idx} style={{
                                display: 'flex',
                                gap: 12,
                                padding: '10px 12px',
                                borderRadius: 10,
                                border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                                background: o.value ? 'rgba(var(--ad-acc-rgb),0.08)' : '#fff',
                                cursor: 'pointer',
                                transition: 'background 0.12s ease',
                                alignItems: 'flex-start',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={o.value}
                                    onChange={(e) => o.setter(e.target.checked)}
                                    style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--ad-acc)' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.94rem', color: '#34383d' }}>
                                        {o.icon} {o.label}
                                    </div>
                                    <div className="muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>{o.hint}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: 14 }}>
                        <button type="button" className="secondary compact" onClick={onCancel} disabled={submitting}>
                            {t('Odustani', 'Cancel')}
                        </button>
                        <button type="button" className="compact" onClick={() => void submit()} disabled={submitting}>
                            {submitting ? t('Pridružujem…', 'Joining…') : `📖 ${t('Pridruži me imeniku', 'Add me to the directory')}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DailyCard({ daily, locale, hasDob }: { daily: DailyPayload | null; locale: Locale; hasDob: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!daily || (!daily.joke && !daily.horoscope)) {
        return null;
    }
    const horo = daily.horoscope;
    const horoText = horo ? (locale === 'en' && horo.text_en ? horo.text_en : horo.text_hr) : null;

    return (
        <div className="bi-daily-grid">
            {daily.joke ? (
                <div className="bi-daily-card bi-daily-joke">
                    <div className="bi-daily-eyebrow">😄 {t('Dosjetka dana', 'Joke of the day')}</div>
                    <p className="bi-daily-text">„{daily.joke.text}"</p>
                    {daily.joke.author ? <p className="bi-daily-author">— {daily.joke.author}</p> : null}
                </div>
            ) : null}
            {horo ? (
                <div className="bi-daily-card bi-daily-horo">
                    <div className="bi-daily-eyebrow">✨ {t('Horoskop dana', 'Horoscope of the day')} · {horo.sign_label}</div>
                    {horo.fallback && !hasDob ? (
                        <p className="bi-daily-author" style={{ marginTop: 0 }}>
                            {t(
                                'Nemam tvoj datum rođenja! Evo kakav bi ti horoskop bio da si:',
                                "I don't have your date of birth! Here's the horoscope you'd get if you were a:",
                            )}{' '}
                            <strong>{horo.sign_label}</strong>
                        </p>
                    ) : null}
                    <p className="bi-daily-text">{horoText ?? t('Horoskop još nije generiran za danas.', 'Horoscope not generated yet for today.')}</p>
                </div>
            ) : null}
        </div>
    );
}

type GlobalSettings = {
    jokes_enabled: boolean;
    horoscope_enabled: boolean;
    birthday_emails_enabled: boolean;
    app_theme?: 'gold' | 'erp' | 'hrms';
    ado_stale_offer_days?: number;
    ado_atrisk_customer_days?: number;
};

type AdminNotifications = {
    notify_unmatched_komisijska_json: boolean;
    notify_expired_reservations: boolean;
    notify_atrisk_digest: boolean;
};

function AdoSalesSettingsAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type S = { ado_stale_offer_days: number; ado_atrisk_customer_days: number; ado_status_active_months: number; ado_status_lost_months: number; ado_f2s_window_months: number };
    const [s, setS] = useState<S | null>(null);

    useEffect(() => {
        api<{ settings: Partial<S> }>('/admin/app-settings', token)
            .then((r) => setS({
                ado_stale_offer_days: r.settings.ado_stale_offer_days ?? 60,
                ado_atrisk_customer_days: r.settings.ado_atrisk_customer_days ?? 90,
                ado_status_active_months: r.settings.ado_status_active_months ?? 18,
                ado_status_lost_months: r.settings.ado_status_lost_months ?? 36,
                ado_f2s_window_months: r.settings.ado_f2s_window_months ?? 6,
            }))
            .catch(() => undefined);
    }, [token]);

    if (!s) return null;

    const save = async (key: keyof S, v: number) => {
        setS({ ...s, [key]: v });
        try { await mutate('/admin/app-settings', token, 'PATCH', { [key]: v }); } catch { /* ignore */ }
    };

    return (
        <Accordion
            title={t('Postavke prodaje (m.1 ADO)', 'Sales settings (m.1 ADO)')}
            subtitle={t('Pragovi statusa klijenta, stale ponuda, F2S window — sustavske postavke.', 'Customer status thresholds, stale offers, F2S window — system-wide.')}
            badge={<span className="bi-super-badge">superadmin</span>}
        >
            <h5 style={{ margin: '0 0 6px', fontSize: '0.82rem', color: 'var(--ad-ink)' }}>📌 {t('CRM i pipeline', 'CRM & pipeline')}</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <ThresholdInput label={t('Stale ponuda (dana)', 'Stale offer (days)')} value={s.ado_stale_offer_days} onSave={(v) => save('ado_stale_offer_days', v)} />
                <ThresholdInput label={t('At-risk klijent (dana)', 'At-risk customer (days)')} value={s.ado_atrisk_customer_days} onSave={(v) => save('ado_atrisk_customer_days', v)} />
            </div>
            <h5 style={{ margin: '0 0 6px', fontSize: '0.82rem', color: 'var(--ad-ink)' }}>💎 {t('Klasifikacija statusa klijenta (mjeseci)', 'Customer status classification (months)')}</h5>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: '0.74rem' }}>
                {t('Aktivan: < X mj od zadnje kupnje · Neaktivan: X – Y · Izgubljen: ≥ Y', 'Active: <X months from last purchase · Inactive: X–Y · Lost: ≥Y')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                <ThresholdInput label={t('Aktivan < (mj)', 'Active < (mo)')} value={s.ado_status_active_months} onSave={(v) => save('ado_status_active_months', v)} />
                <ThresholdInput label={t('Izgubljen ≥ (mj)', 'Lost ≥ (mo)')} value={s.ado_status_lost_months} onSave={(v) => save('ado_status_lost_months', v)} />
                <ThresholdInput label={t('F2S window (mj)', 'F2S window (mo)')} value={s.ado_f2s_window_months} onSave={(v) => save('ado_f2s_window_months', v)} />
            </div>
        </Accordion>
    );
}

type AsanaWorkspace = { gid: string; name: string; is_organization?: boolean };
type AsanaConnection = {
    connected: boolean;
    status?: 'active' | 'invalid' | 'revoked';
    asana_user_gid?: string;
    asana_user_name?: string | null;
    asana_user_email?: string | null;
    default_workspace_gid?: string | null;
    default_workspace_name?: string | null;
    last_verified_at?: string | null;
    last_sync_at?: string | null;
    error_message?: string | null;
    webhook?: { active: boolean; handshaked_at?: string | null; last_event_at?: string | null } | null;
};

/**
 * 2FA (TOTP) — postavljanje/isključivanje dvofaktorske prijave u profilu.
 * Pentest hardening 2026-06-16. Backend: TwoFactorController + AuthController::login.
 */
function TwoFactorAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [status, setStatus] = useState<{ enabled: boolean; pending: boolean; enforced: boolean } | null>(null);
    const [enroll, setEnroll] = useState<{ secret: string; otpauth_uri: string; recovery_codes: string[] } | null>(null);
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const loadStatus = async () => {
        try { setStatus(await api<{ enabled: boolean; pending: boolean; enforced: boolean }>('/2fa/status', token)); } catch { /* ignore */ }
    };
    useEffect(() => { void loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (enroll?.otpauth_uri) {
            QRCode.toDataURL(enroll.otpauth_uri, { margin: 1, width: 200 }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
        } else {
            setQrDataUrl('');
        }
    }, [enroll]);

    const startEnroll = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try { setEnroll(await api('/2fa/enroll', token, { method: 'POST' })); }
        catch (e) { setErr(e instanceof Error ? e.message : 'Greška.'); }
        finally { setBusy(false); }
    };
    const confirm = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try {
            await api('/2fa/confirm', token, { method: 'POST', body: JSON.stringify({ code }) });
            setMsg(t('2FA je uključen ✓', '2FA enabled ✓')); setEnroll(null); setCode(''); await loadStatus();
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška.'); }
        finally { setBusy(false); }
    };
    const disable = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try {
            await api('/2fa/disable', token, { method: 'POST', body: JSON.stringify({ code }) });
            setMsg(t('2FA je isključen.', '2FA disabled.')); setCode(''); await loadStatus();
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška.'); }
        finally { setBusy(false); }
    };

    const subtitle = status?.enabled ? t('Aktivan ✓', 'Active ✓') : t('Dvofaktorska prijava (TOTP)', 'Two-factor sign-in (TOTP)');

    return (
        <Accordion title={t('🔐 2FA — dvofaktorska prijava', '🔐 2FA — two-factor')} subtitle={subtitle} defaultOpen={!!status?.enforced && !status?.enabled}>
            {status?.enforced && !status?.enabled ? (
                <p style={{ color: '#b14040', fontWeight: 600, fontSize: '0.86rem', margin: '0 0 8px' }}>
                    ⚠ {t('2FA je obavezan za tvoju razinu pristupa — postavi ga sada.', '2FA is required for your access level — set it up now.')}
                </p>
            ) : null}

            {status?.enabled ? (
                <div style={{ fontSize: '0.9rem' }}>
                    <p style={{ color: '#1f7a36', fontWeight: 600 }}>✓ {t('2FA je uključen na tvom računu.', '2FA is enabled on your account.')}</p>
                    <p className="muted" style={{ fontSize: '0.82rem' }}>{t('Za isključivanje upiši trenutni kod iz aplikacije (ili recovery kod).', 'To disable, enter the current code from your app (or a recovery code).')}</p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={20}
                            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', width: 160 }} />
                        <button type="button" className="secondary" disabled={busy || !code} onClick={disable}>{t('Isključi 2FA', 'Disable 2FA')}</button>
                    </div>
                </div>
            ) : enroll ? (
                <div style={{ fontSize: '0.9rem' }}>
                    <ol style={{ paddingLeft: 18, margin: '0 0 10px' }}>
                        <li>{t('Skeniraj QR kod u Google Authenticator / Authy / 1Password.', 'Scan the QR in Google Authenticator / Authy / 1Password.')}</li>
                        <li>{t('Upiši 6-znamenkasti kod ispod da potvrdiš.', 'Enter the 6-digit code below to confirm.')}</li>
                    </ol>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        {qrDataUrl ? <img src={qrDataUrl} alt="2FA QR" width={200} height={200} style={{ borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.3)' }} /> : null}
                        <div style={{ minWidth: 220 }}>
                            <p style={{ margin: '0 0 4px', fontSize: '0.8rem' }} className="muted">{t('Ručni unos (ako ne možeš skenirati):', 'Manual entry (if you can’t scan):')}</p>
                            <code style={{ display: 'block', wordBreak: 'break-all', background: '#f8fafc', padding: '6px 8px', borderRadius: 6, fontSize: '0.82rem' }}>{enroll.secret}</code>
                            <p style={{ margin: '10px 0 4px', fontSize: '0.8rem', fontWeight: 600, color: '#b14040' }}>{t('Recovery kodovi (spremi ih sad — prikazuju se samo jednom):', 'Recovery codes (save now — shown only once):')}</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontFamily: 'monospace', fontSize: '0.82rem' }}>
                                {enroll.recovery_codes.map((c) => <span key={c}>{c}</span>)}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} autoFocus
                            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', width: 140 }} />
                        <button type="button" disabled={busy || code.length < 6} onClick={confirm}>{t('Potvrdi i uključi', 'Confirm & enable')}</button>
                        <button type="button" className="link-button" onClick={() => { setEnroll(null); setCode(''); }}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </div>
            ) : (
                <div style={{ fontSize: '0.9rem' }}>
                    <p className="muted" style={{ fontSize: '0.85rem' }}>{t('Dodatni sloj zaštite: pri prijavi se uz lozinku traži i kod iz aplikacije (Google Authenticator i sl.).', 'Extra protection: sign-in asks for a code from your authenticator app in addition to your password.')}</p>
                    <button type="button" disabled={busy} onClick={startEnroll}>{t('Postavi 2FA', 'Set up 2FA')}</button>
                </div>
            )}
            {msg ? <p style={{ color: '#1f7a36', fontSize: '0.82rem', marginTop: 8 }}>{msg}</p> : null}
            {err ? <p className="error" style={{ fontSize: '0.82rem', marginTop: 8 }}>{err}</p> : null}
        </Accordion>
    );
}

/**
 * m.11 Tasks ↔ Asana — per-user PAT konekcija u profilu.
 * - nije spojen: input za PAT + link na Asana stranicu gdje se generira token
 * - spojen: prikaz identiteta + dropdown za default workspace + disconnect
 */
function AsanaConnectionAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [conn, setConn] = useState<AsanaConnection | null>(null);
    const [pat, setPat] = useState('');
    const [workspaces, setWorkspaces] = useState<AsanaWorkspace[] | null>(null);
    const [busy, setBusy] = useState<'connect' | 'verify' | 'workspace' | 'disconnect' | 'webhook' | ''>('');
    const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    const load = async () => {
        try { setConn(await api<AsanaConnection>('/asana/connection', token)); }
        catch { setConn({ connected: false }); }
    };
    useEffect(() => { void load(); }, [token]);

    const connect = async () => {
        if (pat.trim().length < 20) { setMsg({ kind: 'err', text: t('PAT je prekratak', 'PAT too short') }); return; }
        setBusy('connect'); setMsg(null);
        try {
            const r = await mutate<{ ok: boolean; asana_user_name: string; workspaces: AsanaWorkspace[] }>(
                '/asana/connection', token, 'POST', { access_token: pat.trim() });
            setMsg({ kind: 'ok', text: t('Spojen kao ', 'Connected as ') + (r.asana_user_name || '?') });
            setWorkspaces(r.workspaces);
            setPat('');
            await load();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'API' });
        } finally { setBusy(''); }
    };

    const verify = async () => {
        setBusy('verify'); setMsg(null);
        try {
            const r = await mutate<{ ok: boolean; asana_user_name: string; workspaces: AsanaWorkspace[] }>(
                '/asana/connection/verify', token, 'POST', {});
            setMsg({ kind: 'ok', text: t('Token i dalje valjan ✓', 'Token still valid ✓') });
            setWorkspaces(r.workspaces);
            await load();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'API' });
            await load();
        } finally { setBusy(''); }
    };

    const setWorkspace = async (gid: string, name: string) => {
        setBusy('workspace');
        try {
            await mutate('/asana/connection/workspace', token, 'PATCH', { workspace_gid: gid, workspace_name: name });
            await load();
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'API' });
        } finally { setBusy(''); }
    };

    const toggleWebhook = async () => {
        setBusy('webhook'); setMsg(null);
        try {
            const isActive = !!conn?.webhook?.active;
            const r = await mutate<{ ok: boolean; webhook_gid?: string; revoked?: number; message?: string }>(
                isActive ? '/asana/webhook/teardown' : '/asana/webhook/setup', token, 'POST', {});
            if (r.ok) {
                setMsg({ kind: 'ok', text: isActive ? t('Real-time sync isključen', 'Real-time sync off') : t('Real-time sync uključen — Asana će sad slati promjene u BIS automatski', 'Real-time sync on — Asana will push changes to BIS automatically') });
            } else {
                setMsg({ kind: 'err', text: r.message ?? 'Setup failed' });
            }
            await load();
        } catch (e) { setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'API' }); }
        finally { setBusy(''); }
    };

    const disconnect = async () => {
        if (!confirm(t('Odspoji Asana račun? Trebat ćeš ponovo zalijepiti PAT.', 'Disconnect Asana account? You will need to paste the PAT again.'))) return;
        setBusy('disconnect');
        try {
            await mutate('/asana/connection', token, 'DELETE', {});
            setWorkspaces(null);
            setMsg({ kind: 'ok', text: t('Odspojen', 'Disconnected') });
            await load();
        } catch (e) { setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'API' }); }
        finally { setBusy(''); }
    };

    const isConnected = !!conn?.connected;
    const isInvalid = conn?.status === 'invalid';

    return (
        <Accordion
            title={t('🟧 Asana integracija', '🟧 Asana integration')}
            subtitle={isConnected
                ? (t('Spojen kao ', 'Connected as ') + (conn?.asana_user_name || conn?.asana_user_email || '?'))
                : t('Poveži svoj Asana račun da sinkroniziraš taskove s m.11', 'Connect your Asana to sync tasks with m.11')}
            badge={isConnected
                ? (isInvalid
                    ? <span style={{ marginLeft: 8, fontSize: '0.72rem', background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>⚠ {t('token nije valjan', 'invalid token')}</span>
                    : <span style={{ marginLeft: 8, fontSize: '0.72rem', background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>✓ {t('spojen', 'connected')}</span>)
                : <span style={{ marginLeft: 8, fontSize: '0.72rem', background: 'rgba(var(--ad-acc-rgb),0.18)', color: 'var(--ad-acc-3)', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>{t('nije spojeno', 'not connected')}</span>}
            defaultOpen={isConnected}
        >
            {!isConnected ? (
                <div style={{ display: 'grid', gap: 8 }}>
                    <p className="muted" style={{ fontSize: '0.82rem', margin: 0 }}>
                        {t('Otvori ', 'Open ')}
                        <a href="https://app.asana.com/0/my-apps" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ad-acc)', fontWeight: 700 }}>app.asana.com/0/my-apps</a>
                        {t(' → "Create new token" → kopiraj PAT i zalijepi dolje. Token pohranjujemo enkriptiran; nikada se ne vraća natrag.', ' → "Create new token" → copy the PAT and paste below. We store it encrypted; it is never returned to the client.')}
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input type="password" value={pat} onChange={(e) => setPat(e.target.value)} placeholder="1/12345…" autoComplete="off"
                            style={{ flex: 1, padding: '0.5rem 0.6rem', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }} />
                        <button type="button" disabled={busy === 'connect'} onClick={() => void connect()}
                            style={{ background: 'var(--ad-acc)', color: '#fff', border: 0, borderRadius: 6, padding: '0.5rem 1rem', fontWeight: 700, cursor: 'pointer' }}>
                            {busy === 'connect' ? t('Spajam…', 'Connecting…') : t('Spoji', 'Connect')}
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: '0.86rem' }}>
                        <div><strong>{t('Asana korisnik:', 'Asana user:')}</strong> {conn?.asana_user_name || '—'} {conn?.asana_user_email ? <span className="muted">({conn?.asana_user_email})</span> : null}</div>
                        <div><strong>{t('Default workspace:', 'Default workspace:')}</strong> {conn?.default_workspace_name || '—'} <span className="muted" style={{ fontSize: '0.74rem' }}>{conn?.default_workspace_gid ? `(gid ${conn.default_workspace_gid})` : ''}</span></div>
                        {conn?.last_verified_at ? <div className="muted" style={{ fontSize: '0.76rem' }}>{t('Zadnje provjereno:', 'Last verified:')} {String(conn.last_verified_at).replace('T', ' ').slice(0, 19)}</div> : null}
                        {isInvalid && conn?.error_message ? <div style={{ color: '#b14040', fontSize: '0.78rem', marginTop: 4 }}>⚠ {conn.error_message}</div> : null}
                    </div>
                    {workspaces && workspaces.length > 0 ? (
                        <div>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ad-ink)', marginBottom: 4 }}>{t('Odaberi workspace za sync:', 'Pick workspace to sync:')}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {workspaces.map((w) => {
                                    const active = w.gid === conn?.default_workspace_gid;
                                    return (
                                        <button key={w.gid} type="button" disabled={busy === 'workspace'} onClick={() => void setWorkspace(w.gid, w.name)}
                                            style={{ background: active ? '#1f7a36' : 'transparent', color: active ? '#fff' : 'var(--ad-ink)', border: '1px solid ' + (active ? '#1f7a36' : 'rgba(var(--ad-soft-rgb),0.4)'), borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: active ? 700 : 400 }}>
                                            {active ? '✓ ' : ''}{w.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                    <div style={{ padding: '8px 10px', background: conn?.webhook?.active ? 'rgba(31,122,54,0.08)' : 'rgba(var(--ad-soft-rgb),0.10)', borderRadius: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: busy === 'webhook' ? 'wait' : 'pointer', fontSize: '0.84rem' }}>
                            <input type="checkbox" checked={!!conn?.webhook?.active} disabled={busy === 'webhook'} onChange={() => void toggleWebhook()} />
                            <strong>⚡ {t('Real-time sync (webhook)', 'Real-time sync (webhook)')}</strong>
                            {conn?.webhook?.active ? <span style={{ fontSize: '0.7rem', color: '#1f7a36', fontWeight: 700 }}>· {t('aktivno', 'on')}</span> : null}
                        </label>
                        <div className="muted" style={{ fontSize: '0.74rem', marginLeft: 24, marginTop: 2 }}>
                            {t('Bez ovog: moraš ručno kliknuti "Sync s Asanom" da povučeš promjene. S ovim: Asana sama javlja BIS-u kad se nešto promijeni.', 'Without this: you must click "Sync from Asana" manually. With this: Asana pushes events to BIS as they happen.')}
                        </div>
                        {conn?.webhook?.active && conn.webhook?.last_event_at ? (
                            <div className="muted" style={{ fontSize: '0.7rem', marginLeft: 24, marginTop: 2 }}>{t('Zadnji event:', 'Last event:')} {String(conn.webhook.last_event_at).replace('T', ' ').slice(0, 19)}</div>
                        ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" disabled={busy === 'verify'} onClick={() => void verify()}
                            style={{ background: 'transparent', color: 'var(--ad-ink)', border: '1px solid var(--ad-acc)', borderRadius: 6, padding: '0.4rem 0.9rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>
                            {busy === 'verify' ? t('Provjeravam…', 'Verifying…') : t('🔄 Provjeri token + osvježi workspaces', '🔄 Verify token + refresh workspaces')}
                        </button>
                        <button type="button" disabled={busy === 'disconnect'} onClick={() => void disconnect()}
                            style={{ background: 'transparent', color: '#b14040', border: '1px solid #b14040', borderRadius: 6, padding: '0.4rem 0.9rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>
                            {busy === 'disconnect' ? t('Odspajam…', 'Disconnecting…') : t('Odspoji', 'Disconnect')}
                        </button>
                    </div>
                </div>
            )}
            {msg ? <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, fontSize: '0.8rem', background: msg.kind === 'ok' ? 'rgba(31,122,54,0.10)' : 'rgba(177,64,64,0.10)', color: msg.kind === 'ok' ? '#1f7a36' : '#b14040' }}>{msg.text}</div> : null}
        </Accordion>
    );
}

function HedgeToggleAccessAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type U = { id: number; name: string; email: string; role: string; granted: boolean };
    const [users, setUsers] = useState<U[] | null>(null);
    const [busy, setBusy] = useState<number | null>(null);

    useEffect(() => {
        api<{ users: U[] }>('/hedge/automation/users', token).then((r) => setUsers(r.users)).catch(() => setUsers([]));
    }, [token]);

    const toggleGrant = async (u: U) => {
        setBusy(u.id);
        try {
            await mutate('/hedge/automation/grant', token, 'POST', { user_id: u.id, grant: !u.granted });
            setUsers((prev) => (prev ? prev.map((x) => (x.id === u.id ? { ...x, granted: !x.granted } : x)) : prev));
        } catch { /* ignore */ } finally { setBusy(null); }
    };

    if (!users) return null;

    return (
        <Accordion
            title={t('Automatsko hedgiranje — pristup', 'Automatic hedging — access')}
            subtitle={t('Tko smije paliti/gasiti automatic hedging toggle u m.4.', 'Who may flip the automatic hedging toggle in m.4.')}
            badge={<span className="bi-super-badge">superadmin</span>}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {users.map((u) => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, background: u.granted ? 'rgba(31,122,54,0.08)' : 'transparent', cursor: busy === u.id ? 'wait' : 'pointer' }}>
                        <input type="checkbox" checked={u.granted} disabled={busy === u.id} onChange={() => toggleGrant(u)} />
                        <span style={{ fontWeight: u.granted ? 700 : 400 }}>{u.name}</span>
                        <span className="muted" style={{ fontSize: '0.76rem' }}>{u.email} · {u.role}</span>
                    </label>
                ))}
            </div>
        </Accordion>
    );
}

function OwnerNotificationsAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [s, setS] = useState<AdminNotifications | null>(null);

    useEffect(() => {
        api<AdminNotifications>('/warehouse-stock/me/admin-notifications', token)
            .then(setS)
            .catch(() => undefined);
    }, [token]);

    async function toggle(key: keyof AdminNotifications) {
        if (!s) return;
        const next = { ...s, [key]: !s[key] };
        setS(next);
        try {
            await mutate('/warehouse-stock/me/admin-notifications', token, 'PATCH', { [key]: next[key] });
        } catch {
            setS(s);
        }
    }

    return (
        <Accordion
            title={t('Moje admin notifikacije', 'My admin notifications')}
            subtitle={t('m.40.3 cron digesti — samo ti ovo dobivaš', 'm.40.3 cron digests — only you receive these')}
            badge={<span className="bi-super-badge">owner</span>}
        >
            {s ? (
                <div className="bi-toggle-list">
                    <ToggleRow
                        label={t('Komisijska roba prodana iz JSON-a bez matcha', 'Unmatched komisijska sold via JSON')}
                        on={s.notify_unmatched_komisijska_json}
                        onClick={() => void toggle('notify_unmatched_komisijska_json')}
                    />
                    <ToggleRow
                        label={t('Rezervacije koje su istekle (auto-cancel)', 'Reservations that expired (auto-cancel)')}
                        on={s.notify_expired_reservations}
                        onClick={() => void toggle('notify_expired_reservations')}
                    />
                    <ToggleRow
                        label={t('Tjedni at-risk digest (m.1 ADO, ponedjeljkom)', 'Weekly at-risk digest (m.1 ADO, Mondays)')}
                        on={s.notify_atrisk_digest}
                        onClick={() => void toggle('notify_atrisk_digest')}
                    />
                </div>
            ) : null}
        </Accordion>
    );
}

function GlobalSettingsAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [settings, setSettings] = useState<GlobalSettings | null>(null);
    const [tpl, setTpl] = useState<{ subject: string; body_html: string } | null>(null);
    const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        api<{ settings: GlobalSettings }>('/admin/app-settings', token).then((r) => setSettings(r.settings)).catch(() => undefined);
        api<{ template: { subject: string; body_html: string } }>('/admin/email-templates/birthday', token)
            .then((r) => setTpl({ subject: r.template.subject, body_html: r.template.body_html }))
            .catch(() => undefined);
    }, [token]);

    async function toggle(key: keyof GlobalSettings) {
        if (!settings) return;
        const next = { ...settings, [key]: !settings[key] };
        setSettings(next);
        try {
            await mutate('/admin/app-settings', token, 'PATCH', { [key]: next[key] });
        } catch {
            setSettings(settings);
        }
    }

    async function saveTpl() {
        if (!tpl) return;
        setBusy(true);
        setMsg('');
        try {
            await mutate('/admin/email-templates/birthday', token, 'PATCH', tpl);
            setMsg(t('Predložak spremljen.', 'Template saved.'));
        } catch (e) {
            setMsg(e instanceof Error ? e.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function doPreview() {
        if (!tpl) return;
        try {
            const r = await mutate<{ subject: string; html: string }>('/admin/email-templates/birthday/preview', token, 'POST', tpl);
            setPreview(r);
        } catch {
            // ignore
        }
    }

    return (
        <Accordion
            title={t('Globalne postavke', 'Global settings')}
            subtitle={t('Vic / horoskop / rođendanski mailovi — za sve korisnike', 'Jokes / horoscope / birthday emails — for everyone')}
            badge={<span className="bi-super-badge">superadmin</span>}
        >
            {settings ? (
                <div className="bi-toggle-list">
                    <ToggleRow label={t('Dosjetka dana (AI VIC)', 'Joke of the day')} on={settings.jokes_enabled} onClick={() => void toggle('jokes_enabled')} />
                    <ToggleRow label={t('Dnevni horoskop', 'Daily horoscope')} on={settings.horoscope_enabled} onClick={() => void toggle('horoscope_enabled')} />
                    <ToggleRow label={t('Rođendanski mailovi', 'Birthday emails')} on={settings.birthday_emails_enabled} onClick={() => void toggle('birthday_emails_enabled')} />
                </div>
            ) : null}

            {settings ? (
                <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(0,0,0,0.03)', borderRadius: 8 }}>
                    <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem' }}>🎨 {t('Tema aplikacije', 'App theme')}</h4>
                    <p className="muted" style={{ margin: '0 0 10px', fontSize: '0.78rem' }}>
                        {t('Sistemska postavka — vrijedi za SVE korisnike. Reload tab-a za primjenu.', 'System-wide setting — applies to ALL users. Reload tab to apply.')}
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {(['gold', 'erp', 'hrms'] as const).map((theme) => {
                            const isActive = (settings.app_theme ?? 'gold') === theme;
                            const label = theme === 'gold'
                                ? (locale === 'en' ? '🪙 Gold (default)' : '🪙 Auro (default)')
                                : theme === 'erp'
                                    ? (locale === 'en' ? '🏛 ERP (banking)' : '🏛 ERP (poslovno)')
                                    : (locale === 'en' ? '🟣 HRMS (sidebar)' : '🟣 HRMS (sidebar)');
                            return (
                                <button
                                    key={theme}
                                    type="button"
                                    onClick={async () => {
                                        if (isActive) return;
                                        const next = { ...settings, app_theme: theme };
                                        setSettings(next);
                                        try {
                                            await mutate('/admin/app-settings', token, 'PATCH', { app_theme: theme });
                                            if (confirm(t('Tema spremljena. Reload sada da vidiš promjenu?', 'Theme saved. Reload now to see change?'))) {
                                                window.location.reload();
                                            }
                                        } catch (e) {
                                            setSettings(settings);
                                            alert(e instanceof Error ? e.message : 'API');
                                        }
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: isActive ? '2px solid var(--ad-acc)' : '1px solid rgba(0,0,0,0.15)',
                                        background: isActive ? 'rgba(var(--ad-acc-rgb),0.12)' : '#fff',
                                        color: isActive ? 'var(--ad-ink)' : '#444',
                                        cursor: isActive ? 'default' : 'pointer',
                                        fontWeight: isActive ? 700 : 500,
                                    }}
                                >
                                    {label} {isActive ? '✓' : ''}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {tpl ? (
                <div className="bi-tpl-editor">
                    <h4>{t('Rođendanski email — predložak', 'Birthday email — template')}</h4>
                    <p className="muted bi-field-hint">
                        {t('Placeholderi:', 'Placeholders:')} <code>{'{{name}}'}</code> <code>{'{{joke}}'}</code> <code>{'{{joke_author}}'}</code> <code>{'{{year}}'}</code>
                    </p>
                    <label className="bi-field">
                        <span className="bi-field-label">{t('Naslov (subject)', 'Subject')}</span>
                        <input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} />
                    </label>
                    <label className="bi-field">
                        <span className="bi-field-label">{t('HTML sadržaj', 'HTML body')}</span>
                        <textarea
                            rows={12}
                            value={tpl.body_html}
                            onChange={(e) => setTpl({ ...tpl, body_html: e.target.value })}
                            className="bi-tpl-textarea"
                        />
                    </label>
                    <div className="bi-profile-photo-btns">
                        <button type="button" className="compact" disabled={busy} onClick={() => void saveTpl()}>
                            {busy ? t('Spremam…', 'Saving…') : t('Spremi predložak', 'Save template')}
                        </button>
                        <button type="button" className="secondary compact" onClick={() => void doPreview()}>
                            {t('Pregled', 'Preview')}
                        </button>
                    </div>
                    {msg ? <p className="bi-profile-ok">{msg}</p> : null}
                    {preview ? (
                        <div className="bi-tpl-preview">
                            <div className="bi-tpl-preview-subject">{t('Naslov:', 'Subject:')} <strong>{preview.subject}</strong></div>
                            <iframe title="preview" className="bi-tpl-preview-frame" srcDoc={preview.html} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </Accordion>
    );
}

function ThresholdInput({ label, value, onSave }: { label: string; value: number; onSave: (v: number) => void | Promise<void> }) {
    const [v, setV] = useState(String(value));
    useEffect(() => { setV(String(value)); }, [value]);
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
            <span style={{ color: '#666', fontSize: '0.74rem' }}>{label}</span>
            <input
                type="number"
                min={1}
                max={730}
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={() => {
                    const n = parseInt(v, 10);
                    if (Number.isFinite(n) && n >= 1 && n <= 730 && n !== value) void onSave(n);
                    else setV(String(value));
                }}
                style={{ padding: '6px 10px', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 4, fontSize: '0.95rem', fontWeight: 700 }}
            />
        </label>
    );
}

function ToggleRow({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
    return (
        <button type="button" className={`bi-toggle-row ${on ? 'is-on' : ''}`} onClick={onClick}>
            <span>{label}</span>
            <span className={`bi-toggle-switch ${on ? 'is-on' : ''}`}>
                <span className="bi-toggle-knob" />
            </span>
        </button>
    );
}

type ErrorPageConfig = {
    theme: 'minimal' | 'spaceship' | 'custom';
    has_custom: boolean;
    custom_html: string;
    custom_updated_at: string | null;
};

type ErrorPagesPayload = {
    pages: Record<'503' | '404', ErrorPageConfig>;
    available_themes: Array<'minimal' | 'spaceship' | 'custom'>;
};

/**
 * God-mode konfiguracija error stranica (503, 404) — samo vjakomin@gmail.com.
 * Theme select + custom HTML editor + preview u novom tabu.
 */
function ErrorPagesAccordion({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ErrorPagesPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [activeCode, setActiveCode] = useState<'503' | '404'>('503');
    const [customDraft, setCustomDraft] = useState('');

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (data) {
            setCustomDraft(data.pages[activeCode].custom_html);
        }
    }, [data, activeCode]);

    async function load() {
        setLoading(true);
        try {
            const r = await api<ErrorPagesPayload>('/admin/error-pages', token);
            setData(r);
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }

    async function saveTheme(code: '503' | '404', theme: 'minimal' | 'spaceship' | 'custom') {
        setBusy(true); setMsg(''); setErr('');
        try {
            const r = await mutate<ErrorPagesPayload>('/admin/error-pages', token, 'PATCH', { code, theme });
            setData(r);
            setMsg(t('Theme spremljen.', 'Theme saved.'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    async function saveCustomHtml() {
        setBusy(true); setMsg(''); setErr('');
        try {
            const r = await mutate<ErrorPagesPayload>('/admin/error-pages', token, 'PATCH', { code: activeCode, custom_html: customDraft });
            setData(r);
            setMsg(t('Custom HTML spremljen.', 'Custom HTML saved.'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    async function deleteCustomHtml() {
        if (!confirm(t('Obrisati custom HTML?', 'Delete custom HTML?'))) return;
        setBusy(true);
        try {
            const r = await mutate<ErrorPagesPayload>('/admin/error-pages', token, 'PATCH', { code: activeCode, custom_html: null });
            setData(r);
            setCustomDraft('');
            setMsg(t('Custom HTML obrisan. Theme će fallback na minimal ako je bio postavljen na custom.', 'Custom HTML deleted. Theme will fall back to minimal if it was set to custom.'));
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    function openPreview(theme: 'minimal' | 'spaceship' | 'custom') {
        window.open(`/errors-preview/${activeCode}/${theme}`, '_blank', 'noopener,noreferrer');
    }

    async function onFileUpload(file: File) {
        const reader = new FileReader();
        reader.onload = () => {
            const txt = (reader.result as string) ?? '';
            setCustomDraft(txt);
        };
        reader.readAsText(file);
    }

    const themeLabels: Record<'minimal' | 'spaceship' | 'custom', string> = {
        minimal: t('Minimal (default)', 'Minimal (default)'),
        spaceship: t('Svemirska scena', 'Spaceship scene'),
        custom: t('Custom HTML', 'Custom HTML'),
    };

    if (loading) return <Accordion title={t('Error stranice', 'Error pages')} subtitle="god-mode"><p className="muted">{t('Učitavam…', 'Loading…')}</p></Accordion>;
    if (!data) return null;

    const current = data.pages[activeCode];

    return (
        <Accordion title={t('Error stranice (503 / 404)', 'Error pages (503 / 404)')} subtitle={t('Izgled stranica koje korisnik vidi kad sustav padne ili stranica ne postoji', 'How error pages look to users')}>
            {msg ? <p style={{ color: '#1f7a36', fontSize: '0.85rem', margin: '4px 0' }}>✓ {msg}</p> : null}
            {err ? <p className="error">{err}</p> : null}

            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {(['503', '404'] as const).map((code) => (
                    <button
                        key={code}
                        type="button"
                        onClick={() => setActiveCode(code)}
                        style={{
                            padding: '6px 12px',
                            borderRadius: 8,
                            border: activeCode === code ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            background: activeCode === code ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                            color: activeCode === code ? '#fffbeb' : 'var(--ad-ink)',
                            fontWeight: 600,
                            fontSize: '0.86rem',
                            cursor: 'pointer',
                        }}
                    >
                        {code} {code === '503' ? t('(održavanje)', '(maintenance)') : t('(ne pronađeno)', '(not found)')}
                    </button>
                ))}
            </div>

            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10, padding: 14 }}>
                <p style={{ margin: '0 0 10px', fontSize: '0.84rem', color: '#34383d' }}>
                    <strong>{t('Aktivni theme za', 'Active theme for')} {activeCode}:</strong> {themeLabels[current.theme]}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.available_themes.map((th) => {
                        const isActive = current.theme === th;
                        const isCustom = th === 'custom';
                        return (
                            <div key={th} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 12px',
                                borderRadius: 8,
                                background: isActive ? 'rgba(31,122,54,0.08)' : 'rgba(255,255,255,0.5)',
                                border: `1px solid ${isActive ? 'rgba(31,122,54,0.3)' : 'rgba(var(--ad-soft-rgb),0.2)'}`,
                            }}>
                                <input
                                    type="radio"
                                    name={`theme-${activeCode}`}
                                    checked={isActive}
                                    onChange={() => void saveTheme(activeCode, th)}
                                    disabled={busy || (isCustom && !current.has_custom)}
                                    style={{ accentColor: 'var(--ad-acc)' }}
                                />
                                <span style={{ flex: 1, fontWeight: isActive ? 600 : 400, fontSize: '0.88rem' }}>
                                    {themeLabels[th]}
                                    {isCustom && !current.has_custom ? <span className="muted" style={{ marginLeft: 6, fontSize: '0.78rem' }}>({t('još nema custom HTML', 'no custom HTML yet')})</span> : null}
                                </span>
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() => openPreview(th)}
                                    disabled={isCustom && !current.has_custom}
                                    style={{ fontSize: '0.78rem' }}
                                >
                                    👁 {t('Preview', 'Preview')}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginTop: 14, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ fontSize: '0.9rem' }}>{t('Custom HTML za', 'Custom HTML for')} {activeCode}</strong>
                    {current.custom_updated_at ? (
                        <span className="muted" style={{ fontSize: '0.76rem' }}>
                            {t('Posljednja izmjena', 'Last updated')}: {new Date(current.custom_updated_at).toLocaleString('hr-HR')}
                        </span>
                    ) : null}
                </div>
                <p className="muted" style={{ fontSize: '0.78rem', margin: '0 0 8px' }}>
                    {t(
                        'Unesi puni HTML dokument (<!DOCTYPE html>...). Spremanje aktivira theme „Custom HTML" iznad. Možeš i učitati iz datoteke.',
                        'Paste full HTML document (<!DOCTYPE html>...). Saving activates the "Custom HTML" theme above. You can also load from a file.',
                    )}
                </p>
                <input
                    type="file"
                    accept=".html,.htm,text/html"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void onFileUpload(f);
                        e.target.value = '';
                    }}
                    style={{ fontSize: '0.82rem', marginBottom: 8 }}
                />
                <textarea
                    value={customDraft}
                    onChange={(e) => setCustomDraft(e.target.value)}
                    placeholder="<!DOCTYPE html>\n<html>\n  …\n</html>"
                    style={{
                        width: '100%',
                        minHeight: 200,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.78rem',
                        padding: 10,
                        border: '1px solid rgba(var(--ad-soft-rgb),0.3)',
                        borderRadius: 8,
                        resize: 'vertical',
                    }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                    {current.has_custom ? (
                        <button type="button" className="secondary compact" onClick={() => void deleteCustomHtml()} disabled={busy} style={{ color: '#b14040' }}>
                            🗑 {t('Obriši custom', 'Delete custom')}
                        </button>
                    ) : null}
                    <button type="button" className="compact" onClick={() => void saveCustomHtml()} disabled={busy || customDraft === current.custom_html}>
                        💾 {t('Spremi custom HTML', 'Save custom HTML')}
                    </button>
                </div>
            </div>
        </Accordion>
    );
}
